import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  TableCell,
  TableRow,
  Menu,
  MenuItem,
  IconButton,
  FormControl,
  Select,
  LinearProgress,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterHeader from "../../components/FilterHeader";
import * as XLSX from "xlsx";
import AutoSizer from "react-virtualized-auto-sizer";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import EmployeeProfile from "./EmployeeProfile";
import { useEmployeeService } from "../../services/EmployeeService";

const columnDefs = [
  { key: "Group_Employee_Code", label: "Emp Code", flex: 1 },
  { key: "Full_Name", label: "Full Name", flex: 2 },
  { key: "HR_Manager_Code", label: "HR Emp Code", flex: 1 },
  { key: "HR_Manager_Name", label: "HR Manager", flex: 1.5 },
  { key: "Official_Email_ID", label: "Email", flex: 2 },
  { key: "Mobile", label: "Mobile", flex: 1 },
  { key: "Business_Vertical_Desc", label: "Vertical", flex: 1 },
  {
    key: "Business_Sub_Vertical_Description",
    label: "Sub Vertical",
    flex: 2.5,
  },
  // { key: "AssignedLocation", label: "Location", flex: 1 },
  { key: "FaceDataStatus", label: "Face Status", flex: 1 },
  { key: "CheckedInAtleastOnce", label: "Checked-in Once", flex: 0.5 },
];

const ROW_HEIGHT = 32;

const EmployeeList = () => {
  const { fetchEmployees } = useEmployeeService();
  const [filters, setFilters] = useState({
    VerticalDescriptions: "All",
    ProjectDescriptions: "All",
    Locations: "All",
  });

  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedEmpID, setSelectedEmpID] = useState(null);
  const [search, setSearch] = useState({});
  const [data, setData] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const listRef = useRef();

  const handleProfileOpen = (empID) => {
    setSelectedEmpID(empID);
    setProfileOpen(true);
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
    setSelectedEmpID(null);
  };

  useEffect(() => {
    fetchEmployeesList(false);
  }, []);

  const fetchEmployeesList = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const res = await fetchEmployees(forceRefresh);
      setData(res);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearch({}); // Clear column filters when top filters change
  }, [filters]);

  const handleSearch = (key, value) => {
    setSearch((prev) => ({ ...prev, [key]: value }));
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchVertical =
        filters.VerticalDescriptions === "All" ||
        item.Business_Vertical_Desc === filters.VerticalDescriptions;
      const matchProject =
        filters.ProjectDescriptions === "All" ||
        item.Project_Description === filters.ProjectDescriptions;
      const matchLocation =
        filters.Locations === "All" ||
        item.Business_Sub_Vertical_Description === filters.Locations;

      const matchesSearch = columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        if (!term) return true; // no filter for this column

        const value = item[key];

        // Special handling for binary (1/0) fields shown as Yes/No
        if (key === "CheckedInAtleastOnce") {
          const isYes = term === "yes" || term === "1";
          const isNo = term === "no" || term === "0";
          return (value === 1 && isYes) || (value === 0 && isNo);
        }

        return value?.toString().toLowerCase().includes(term);
      });

      return matchVertical && matchProject && matchLocation && matchesSearch;
    });
  }, [data, filters, search]);

  const menuRef = useRef(null);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    const isActive = activeRowIndex === index;

    return (
      <Box
        key={index}
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          //  backgroundColor: isActive ? "#f5f5f5" : "inherit",
        }}
      >
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
              fontSize: "0.875rem",
            }}
          >
            {key === "FaceDataStatus" ? (
              <Chip
                label={row[key]}
                size="small"
                color={
                  row[key] === "Approved"
                    ? "success"
                    : row[key] === "Yet to upload"
                    ? "#f5f5f5"
                    : row[key] === "Rejected"
                    ? "error"
                    : "warning"
                }
              />
            ) : key === "CheckedInAtleastOnce" || key === "loggedinOnce" ? (
              <Chip
                label={row[key] === 1 ? "Yes" : "No"}
                size="small"
                color={row[key] === 1 ? "success" : "error"}
              />
            ) : key === "Group_Employee_Code" ? (
              <Typography
                variant="body2"
                // color="primary"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => handleProfileOpen(row.Group_Employee_Code)}
              >
                {row[key]}
              </Typography>
            ) : (
              row[key]
            )}
          </Box>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveRowIndex(null); // close menu
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.writeFile(wb, "EmployeeList.xlsx");
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        {/* Left: Title */}
        <Typography variant="h2" sx={{ minWidth: 180 }}>
          List of Employees
        </Typography>

        {/* Right: Filters + Export */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "flex-end", // 👈 aligns filters+button to the right
            minWidth: "60%", // 👈 lets it occupy more space
          }}
        >
          <FilterHeader filters={filters} setFilters={setFilters} />
          <Button variant="contained" onClick={handleExport}>
            Export to Excel
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />
      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error">Error: {error}</Typography>
      ) : (
        <Box component={Paper} sx={{ overflow: "auto" }}>
          {/* Header Row */}
          <Box sx={{ display: "flex", p: 0.5, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            ))}

            <Box sx={{ width: 40, flexShrink: 0 }} />
          </Box>
          {filteredData.length > 0 ? (
            <Box sx={{ height: "70vh" }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemSize={() => ROW_HEIGHT}
                    itemCount={filteredData.length}
                    itemKey={(index) => filteredData[index].Id || index}
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          ) : (
            <Box p={2} textAlign="center" color="text.secondary">
              No matching records found.
            </Box>
          )}
        </Box>
      )}

     <Dialog
        open={profileOpen}
        onClose={handleProfileClose}
        maxWidth={false} // disables fixed maxWidth
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '85%',
            maxWidth: '85%',
          },
        }}
      >
        {/* <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0 }}>
          <IconButton onClick={handleProfileClose}>
            <span style={{ fontSize: "1.2rem" }}>✕</span>
          </IconButton>
        </Box> */}

        <DialogContent sx={{ px: 1, pb: 1, pt: 0 }}>
          <Box sx={{ display: "block", width: "100%", minWidth: 0 }}>
            <EmployeeProfile empID={selectedEmpID} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EmployeeList;
